/**
 * TrainingProfilesList Page
 * Lists ownable heat pump simulation training profiles and exposes create,
 * publish, share, preview, and delete entry points.
 *
 * @module TrainingProfilesList
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  message,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  ExperimentOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import PagesWrapper from '../../PagesWrapper';
import trainingProfilesService from '../../services/trainingProfilesService';
import styles from './TrainingProfiles.module.css';

const { Search } = Input;
const { Text, Title } = Typography;

/**
 * Main training profile management list.
 * @returns {JSX.Element} Training profile table with management actions.
 */
const TrainingProfilesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchProfiles = useCallback(async (page = 1, search = searchText) => {
    setLoading(true);
    try {
      const response = await trainingProfilesService.getTrainingProfiles({
        page,
        pageSize: pagination.pageSize,
        search: search || undefined,
      });
      setProfiles(response.items || []);
      setPagination(prev => ({
        ...prev,
        current: response.page || page,
        total: response.totalCount || 0,
      }));
    } catch (error) {
      message.error(t('trainingProfiles.fetchError', 'Failed to load training profiles'));
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, searchText, t]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleSearch = (value) => {
    setSearchText(value);
    fetchProfiles(1, value);
  };

  const handleRefresh = () => {
    fetchProfiles(pagination.current, searchText);
  };

  const handleTableChange = (paginationConfig) => {
    fetchProfiles(paginationConfig.current, searchText);
  };

  const handleTogglePublish = async (profile) => {
    try {
      if (profile.isPublished) {
        await trainingProfilesService.unpublishTrainingProfile(profile.id);
        message.success(t('trainingProfiles.unpublishSuccess', 'Training profile unpublished'));
      } else {
        await trainingProfilesService.publishTrainingProfile(profile.id);
        message.success(t('trainingProfiles.publishSuccess', 'Training profile published'));
      }
      fetchProfiles(pagination.current, searchText);
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || t('trainingProfiles.publishError', 'Failed to update publish status'));
    }
  };

  const handleDelete = async (profile) => {
    try {
      await trainingProfilesService.deleteTrainingProfile(profile.id);
      message.success(t('trainingProfiles.deleteSuccess', 'Training profile deleted'));
      fetchProfiles(pagination.current, searchText);
    } catch (error) {
      message.error(t('trainingProfiles.deleteError', 'Failed to delete training profile'));
    }
  };

  const handleCopyLearnerLink = (profile) => {
    const url = `${window.location.origin}/simulations/heat-pump-training?profileId=${profile.id}`;
    navigator.clipboard.writeText(url);
    message.success(t('trainingProfiles.linkCopied', 'Training profile link copied'));
  };

  const handleOpenTestPreview = (profile) => {
    window.open(`/simulations/heat-pump-training-preview?profileId=${profile.id}`, '_blank');
  };

  const columns = [
    {
      title: t('trainingProfiles.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/training-profiles/${record.id}`)}>
          {text}
        </Button>
      ),
    },
    {
      title: t('trainingProfiles.language', 'Language'),
      dataIndex: 'countryLanguage',
      key: 'countryLanguage',
      width: 180,
      render: (_, record) => record.countryLanguage ? (
        <Space direction="vertical" size={0}>
          <span>{record.countryLanguage}</span>
          {record.countryName && <Text type="secondary">{record.countryName}</Text>}
        </Space>
      ) : (record.countryName || <Text type="secondary">—</Text>),
    },
    {
      title: t('trainingProfiles.content', 'Content'),
      key: 'content',
      width: 170,
      render: (_, record) => (
        <Space size="small" wrap>
          <Tag color="green">{record.tutorialQuestCount || 0} {t('trainingProfiles.questsLabel', 'quests')}</Tag>
          <Tag color="purple">{record.quizCollectionCount || 0} {t('trainingProfiles.quizzesLabel', 'quizzes')}</Tag>
        </Space>
      ),
    },
    {
      title: t('trainingProfiles.status', 'Status'),
      dataIndex: 'isPublished',
      key: 'isPublished',
      width: 120,
      render: (isPublished) => isPublished
        ? <Tag color="success" icon={<CheckCircleOutlined />}>{t('trainingProfiles.published', 'Published')}</Tag>
        : <Tag icon={<CloseCircleOutlined />}>{t('trainingProfiles.draft', 'Draft')}</Tag>,
    },
    {
      title: t('trainingProfiles.owner', 'Owner'),
      dataIndex: 'ownerName',
      key: 'ownerName',
      width: 180,
      render: (value) => value || <Text type="secondary">—</Text>,
    },
    {
      title: t('trainingProfiles.updated', 'Updated'),
      dataIndex: 'dateModified',
      key: 'dateModified',
      width: 170,
      render: (value) => value ? new Date(value).toLocaleString() : '—',
    },
    {
      title: t('common.actions', 'Actions'),
      key: 'actions',
      width: 260,
      render: (_, record) => (
        <Space size="small" className={styles.actions}>
          <Tooltip title={t('common.edit', 'Edit')}>
            <Button type="text" icon={<EditOutlined />} onClick={() => navigate(`/training-profiles/${record.id}`)} />
          </Tooltip>
          <Tooltip title={record.isPublished ? t('trainingProfiles.unpublish', 'Unpublish') : t('trainingProfiles.publish', 'Publish')}>
            <Button
              type="text"
              icon={record.isPublished ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => handleTogglePublish(record)}
            />
          </Tooltip>
          <Tooltip title={t('trainingProfiles.openTestPreview', 'Open test preview')}>
            <Button
              type="text"
              icon={<ExperimentOutlined />}
              onClick={() => handleOpenTestPreview(record)}
            />
          </Tooltip>
          <Tooltip title={t('trainingProfiles.openLearnerView', 'Open learner view')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => window.open(`/simulations/heat-pump-training?profileId=${record.id}`, '_blank')}
              disabled={!record.isPublished}
            />
          </Tooltip>
          <Tooltip title={t('trainingProfiles.copyLearnerLink', 'Copy learner link')}>
            <Button type="text" icon={<CopyOutlined />} onClick={() => handleCopyLearnerLink(record)} />
          </Tooltip>
          <Popconfirm
            title={t('trainingProfiles.deleteConfirm', 'Delete this training profile?')}
            onConfirm={() => handleDelete(record)}
            okText={t('common.yes', 'Yes')}
            cancelText={t('common.no', 'No')}
          >
            <Tooltip title={t('common.delete', 'Delete')}>
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PagesWrapper>
      <div className={styles.trainingProfilesContainer}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <ExperimentOutlined className={styles.titleIcon} />
            <Title level={2} className={styles.title}>
              {t('trainingProfiles.title', 'Training Profiles')}
            </Title>
          </div>
          <Text type="secondary">
            {t('trainingProfiles.subtitle', 'Create profile-enabled heat pump training scenarios with DB tutorials and assigned skill quizzes.')}
          </Text>
        </div>

        <div className={styles.toolbar}>
          <Space>
            <Search
              allowClear
              placeholder={t('trainingProfiles.searchPlaceholder', 'Search training profiles')}
              onSearch={handleSearch}
              style={{ width: 300 }}
            />
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              {t('common.refresh', 'Refresh')}
            </Button>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/training-profiles/new')}>
            {t('trainingProfiles.create', 'Create Training Profile')}
          </Button>
        </div>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={profiles}
          pagination={pagination}
          onChange={handleTableChange}
        />
      </div>
    </PagesWrapper>
  );
};

export default TrainingProfilesList;
